import { useState } from "react";
import { Download, ExternalLink, FileText, Loader2, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

function RecordedFile({ id, index, name, hasKey }: { id: string; index: number; name: string; hasKey: boolean }) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // Never use historical /objects URLs from a cached generation response.
  const href = `/api/admin/generated-quotes/${encodeURIComponent(id)}/files/${index}`;
  const download = async () => {
    setPending(true);
    setError(null);
    try {
      const response = await fetch(`${href}?download=1`, { credentials: "include", headers: { Accept: "application/json" } });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || "This file could not be downloaded. Please try again.");
      }
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "This file could not be downloaded. Please try again.");
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="rounded-md border p-3 space-y-2">
      <p className="flex items-start gap-2 text-sm break-all"><FileText className="h-4 w-4 shrink-0" />{name}</p>
      {hasKey ? <div className="flex gap-3 items-center">
        <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline">
          <ExternalLink className="h-3 w-3" /> Open file
        </a>
        <Button size="sm" variant="outline" disabled={pending} onClick={download} className="gap-1 h-7 text-xs">
          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Download
        </Button>
      </div> : <p className="text-xs text-muted-foreground">No stored file is recorded for this upload.</p>}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function GeneratedQuoteFiles({ id, names, keys }: { id: string; names: string[]; keys: string[] }) {
  const count = Math.max(names.length, keys.length);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-1.5"><Paperclip className="h-4 w-4" /> Uploaded Files ({count})</CardTitle>
        <CardDescription>Recorded uploads for this generation. PDFs and supported images open in your browser.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {count === 0 ? <p className="text-sm text-muted-foreground">No files were recorded for this generation.</p>
          : Array.from({ length: count }, (_, index) => <RecordedFile key={`${id}:${index}`} id={id} index={index} name={names[index] || `Upload ${index + 1}`} hasKey={!!keys[index]} />)}
      </CardContent>
    </Card>
  );
}
